/* Multi-tenant onboarding wizard voor kinderopvangorganisaties.
 * Herbruikt bestaande UI-componenten (Card, Input, Button, Dialog).
 * Progressive save per stap; auto-save via debounce.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Building2, Users2, MapPin, Briefcase, FileText, CreditCard, ShieldCheck, ChevronLeft, ChevronRight, Check, CircleDashed, Send, AlertTriangle, PenTool } from "lucide-react";

const STEPS = [
  { key: "bedrijfsgegevens", label: "Bedrijfsgegevens", icon: Building2 },
  { key: "tekenbevoegde", label: "Tekenbevoegde & contacten", icon: Users2 },
  { key: "branding", label: "Branding", icon: FileText },
  { key: "bank", label: "Facturerende entiteit & bank", icon: CreditCard },
  { key: "locaties", label: "Locaties (via Instellingen)", icon: MapPin, external: "/app/instellingen" },
  { key: "groepen", label: "Groepen (via Instellingen)", icon: MapPin, external: "/app/instellingen" },
  { key: "openingstijden", label: "Openingstijden", icon: CircleDashed },
  { key: "contracten", label: "Contractpakketten & tarieven", icon: Briefcase },
  { key: "algemene_voorwaarden", label: "Algemene voorwaarden", icon: FileText, policy: "av" },
  { key: "privacyverklaring", label: "Privacyverklaring", icon: FileText, policy: "privacy" },
  { key: "pedagogisch_beleid", label: "Pedagogisch beleidsplan", icon: FileText, policy: "ppb" },
  { key: "veiligheid_gezondheid", label: "Veiligheids- & gezondheidsbeleid", icon: FileText, policy: "vg" },
  { key: "klachten", label: "Klachtenregeling", icon: FileText, policy: "klachten" },
  { key: "plaatsingsovk", label: "Plaatsingsovereenkomst", icon: FileText, policy: "plaatsingsovk" },
  { key: "gebruikers", label: "Eerste gebruikers (via Instellingen)", icon: Users2, external: "/app/instellingen" },
  { key: "toestemming", label: "Toestemmingsformulieren", icon: FileText, policy: "foto_toestemming" },
  { key: "digitaal_ondertekenen", label: "Digitaal ondertekenen (SignRequest)", icon: PenTool },
  { key: "migratie", label: "Migratie uit ander systeem", icon: CircleDashed },
];

const StatusIcon = ({ status }) => {
  if (status === "compleet" || status === "goedgekeurd") return <Check className="h-4 w-4 text-emerald-600" />;
  if (status === "gedeeltelijk") return <CircleDashed className="h-4 w-4 text-amber-500" />;
  if (status === "afgekeurd") return <AlertTriangle className="h-4 w-4 text-red-600" />;
  return <CircleDashed className="h-4 w-4 text-neutral-300" />;
};

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState(null);
  const [org, setOrg] = useState(null);
  const [session, setSession] = useState(null);
  const [activeStep, setActiveStep] = useState("bedrijfsgegevens");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/organizations");
        const items = r.data.items || [];
        if (!items.length) {
          toast.error("Geen organisatie gevonden");
          return;
        }
        const first = items[0];
        setOrgId(first.id);
        setOrg(first);
        const s = await api.get(`/organizations/${first.id}/onboarding`);
        setSession(s.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveStep = async (stepKey, data, status) => {
    if (!orgId) return;
    try {
      const r = await api.put(`/organizations/${orgId}/onboarding`, { step_key: stepKey, data, status });
      setSession(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    if (!confirm("Weet je zeker dat je alle gegevens wilt indienen ter beoordeling?")) return;
    try {
      await api.post(`/organizations/${orgId}/onboarding/submit`, { verklaring_akkoord: true });
      toast.success("Ingediend ter beoordeling ✔");
      const s = await api.get(`/organizations/${orgId}/onboarding`);
      setSession(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="p-8 text-center text-neutral-500">Onboarding laden…</div>;
  if (!org || !session) return <div className="p-8 text-center text-neutral-500">Geen onboardingsessie beschikbaar.</div>;

  const progress = session.progress || { percent: 0, done: 0, total_required: 0 };
  const stepStatuses = session.step_statuses || {};
  const stepData = session.step_data || {};
  const currentStep = STEPS.find(s => s.key === activeStep);

  return (
    <div data-testid="onboarding-page">
      <PageHeader
        title="Onboarding"
        subtitle={`${org.name} · status: ${session.status}`}
        testid="onboarding-header"
      />

      {/* Voortgang */}
      <Card className="p-4 mb-4 bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-200 shadow-none" data-testid="onb-progress">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm text-neutral-700">
              Voortgang: <strong className="text-neutral-900">{progress.percent}%</strong> ({progress.done}/{progress.total_required} verplichte stappen)
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Automatisch opgeslagen{session.last_saved_at ? ` · laatst: ${new Date(session.last_saved_at).toLocaleString("nl-NL")}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="onb-status">{session.status}</Badge>
            {session.status === "concept" && progress.percent === 100 && user?.role === "superadmin" && (
              <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="onb-submit">
                <Send className="h-4 w-4 mr-1" /> Indienen ter beoordeling
              </Button>
            )}
            {session.status === "ingediend" && (
              <Badge className="bg-amber-100 text-amber-800 border-transparent">Wacht op beoordeling</Badge>
            )}
            {session.status === "actief" && <Badge className="bg-emerald-100 text-emerald-800 border-transparent">Actief</Badge>}
          </div>
        </div>
        <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-all"
               style={{ width: `${Math.max(0, Math.min(100, progress.percent || 0))}%` }} />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Sidebar stappen */}
        <Card className="p-2 bg-white shadow-none max-h-[70vh] overflow-y-auto" data-testid="onb-steps-list">
          {STEPS.map((s, idx) => {
            const Ic = s.icon;
            const st = stepStatuses[s.key] || "nog_niet";
            const isActive = s.key === activeStep;
            return (
              <button
                key={s.key}
                onClick={() => setActiveStep(s.key)}
                data-testid={`onb-step-${s.key}`}
                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 text-sm ${isActive ? "bg-[color:var(--sage-100)] text-[color:var(--sage)] font-medium" : "hover:bg-neutral-50 text-neutral-700"}`}
              >
                <span className="w-5 text-[10px] text-neutral-400">{idx + 1}</span>
                <Ic className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{s.label}</span>
                <StatusIcon status={st} />
              </button>
            );
          })}
        </Card>

        {/* Actieve stap */}
        <Card className="p-5 bg-white shadow-none" data-testid={`onb-panel-${activeStep}`}>
          <StepPanel
            step={currentStep}
            data={stepData[activeStep] || {}}
            org={org}
            orgId={orgId}
            saveStep={saveStep}
            navigate={navigate}
          />
        </Card>
      </div>
    </div>
  );
}

function StepPanel({ step, data, org, orgId, saveStep, navigate }) {
  const [form, setForm] = useState(data);
  useEffect(() => { setForm(data); }, [step.key]); // eslint-disable-line

  if (step.external) {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-2">{step.label}</h3>
        <p className="text-sm text-neutral-600 mb-3">Dit onderdeel wordt in de bestaande instellingen beheerd om dubbele registratie te voorkomen.</p>
        <div className="flex gap-2">
          <Button onClick={() => navigate(step.external)} style={{ background: "var(--sage)" }} className="text-white">
            Open Instellingen
          </Button>
          <Button variant="outline" onClick={() => saveStep(step.key, { markedComplete: true }, "compleet")} data-testid={`onb-mark-complete-${step.key}`}>
            Markeer als klaar
          </Button>
        </div>
      </div>
    );
  }

  if (step.key === "bedrijfsgegevens") return <OrgBusinessStep step={step} form={form} setForm={setForm} orgId={orgId} saveStep={saveStep} />;
  if (step.key === "tekenbevoegde") return <ContactPersonsStep step={step} orgId={orgId} org={org} saveStep={saveStep} />;
  if (step.key === "branding") return <BrandingStep step={step} orgId={orgId} org={org} saveStep={saveStep} />;
  if (step.key === "bank") return <BankStep step={step} orgId={orgId} saveStep={saveStep} />;
  if (step.key === "openingstijden") return <OpeningHoursStep step={step} form={form} setForm={setForm} saveStep={saveStep} />;
  if (step.key === "contracten") return <ContractsStep step={step} orgId={orgId} saveStep={saveStep} />;
  if (step.policy) return <PolicyDocumentStep step={step} orgId={orgId} saveStep={saveStep} />;
  if (step.key === "migratie") return <MigrationStep step={step} form={form} setForm={setForm} saveStep={saveStep} orgId={orgId} />;
  if (step.key === "digitaal_ondertekenen") return <SigningStep step={step} orgId={orgId} saveStep={saveStep} />;
  return <div className="text-sm text-neutral-500">Deze stap wordt binnenkort uitgewerkt.</div>;
}

function OrgBusinessStep({ step, form, setForm, orgId, saveStep }) {
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    // Also patch the organization
    try {
      await api.patch(`/organizations/${orgId}`, {
        statutaire_naam: form.statutaire_naam,
        rechtsvorm: form.rechtsvorm,
        kvk: form.kvk, rsin: form.rsin,
        vestigingsadres: form.vestigingsadres || {},
        telefoon: form.telefoon, email: form.email, website: form.website,
      });
    } catch (e) { toast.error(formatApiError(e)); return; }
    const compleet = !!(form.statutaire_naam && form.kvk && form.vestigingsadres?.postcode && form.email);
    await saveStep(step.key, form, compleet ? "compleet" : "gedeeltelijk");
    toast.success("Opgeslagen");
  };
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Statutaire naam *" val={form.statutaire_naam} onChange={v => upd("statutaire_naam", v)} testid="bg-statnaam" />
        <Field label="Handelsnaam" val={form.handelsnaam} onChange={v => upd("handelsnaam", v)} testid="bg-handel" />
        <div>
          <Label className="text-xs">Rechtsvorm</Label>
          <Select value={form.rechtsvorm || ""} onValueChange={v => upd("rechtsvorm", v)}>
            <SelectTrigger data-testid="bg-rechtsvorm"><SelectValue placeholder="Kies…" /></SelectTrigger>
            <SelectContent className="bg-white">
              {["BV", "VOF", "Eenmanszaak", "Stichting", "Vereniging"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Field label="KVK-nummer * (8 cijfers)" val={form.kvk} onChange={v => upd("kvk", v)} testid="bg-kvk" />
        <Field label="RSIN" val={form.rsin} onChange={v => upd("rsin", v)} testid="bg-rsin" />
      </div>

      <h4 className="text-sm font-medium mt-4">Vestigingsadres</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Straat + huisnr" val={form.vestigingsadres?.straat} onChange={v => upd("vestigingsadres", { ...(form.vestigingsadres || {}), straat: v })} testid="bg-straat" />
        <Field label="Postcode *" val={form.vestigingsadres?.postcode} onChange={v => upd("vestigingsadres", { ...(form.vestigingsadres || {}), postcode: v })} testid="bg-postcode" />
        <Field label="Plaats" val={form.vestigingsadres?.plaats} onChange={v => upd("vestigingsadres", { ...(form.vestigingsadres || {}), plaats: v })} testid="bg-plaats" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Telefoon" val={form.telefoon} onChange={v => upd("telefoon", v)} testid="bg-tel" />
        <Field label="E-mail *" val={form.email} onChange={v => upd("email", v)} testid="bg-email" />
        <Field label="Website" val={form.website} onChange={v => upd("website", v)} testid="bg-web" />
      </div>
      <Button onClick={save} style={{ background: "var(--sage)" }} className="text-white" data-testid="bg-save">Opslaan</Button>
    </div>
  );
}

function ContactPersonsStep({ step, orgId, org, saveStep }) {
  // Repeatable tekenbevoegden-lijst (sectie 4). Migreer legacy singleton
  // 'tekenbevoegde' naar de nieuwe lijst wanneer die leeg is.
  const initTekenb = () => {
    const list = org.tekenbevoegden || [];
    if (list.length > 0) return list;
    const legacy = org.tekenbevoegde || {};
    if (legacy.naam || legacy.email) {
      const [voornaam, ...rest] = (legacy.naam || "").split(" ");
      return [{
        voornaam: voornaam || "",
        achternaam: rest.join(" "),
        functie: legacy.functie || "",
        email: legacy.email || "",
        telefoon: legacy.telefoon || "",
        bevoegdheid_type: "zelfstandig",
        primair_aanspreekpunt: true,
        actief: true,
      }];
    }
    return [{ voornaam: "", achternaam: "", functie: "", email: "", telefoon: "",
              bevoegdheid_type: "zelfstandig", primair_aanspreekpunt: true, actief: true }];
  };
  const [tekenb, setTekenb] = useState(initTekenb);
  const [fin, setFin] = useState(org.contact_financieel || {});
  const [priv, setPriv] = useState(org.contact_privacy || {});
  const [saving, setSaving] = useState(false);

  const primairCount = tekenb.filter(t => t.primair_aanspreekpunt).length;

  const updT = (idx, patch) => setTekenb(list =>
    list.map((t, i) => i === idx ? { ...t, ...patch } : t));

  const setPrimair = (idx) => setTekenb(list =>
    list.map((t, i) => ({ ...t, primair_aanspreekpunt: i === idx })));

  const addT = () => setTekenb(list => [...list, {
    voornaam: "", achternaam: "", functie: "", email: "", telefoon: "",
    bevoegdheid_type: "zelfstandig",
    primair_aanspreekpunt: list.length === 0,  // eerste = automatisch primair
    actief: true,
  }]);

  const removeT = (idx) => {
    if (tekenb.length === 1) { toast.error("Minimaal één tekenbevoegde vereist"); return; }
    setTekenb(list => list.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (primairCount > 1) { toast.error("Maximaal één primair aanspreekpunt"); return; }
    setSaving(true);
    try {
      await api.put(`/organizations/${orgId}/tekenbevoegden`, { tekenbevoegden: tekenb });
      await api.patch(`/organizations/${orgId}`, {
        contact_financieel: fin, contact_privacy: priv,
      });
      const compleet = tekenb.every(t => t.voornaam && t.achternaam && t.email);
      await saveStep(step.key, { tekenb, fin, priv }, compleet ? "compleet" : "gedeeltelijk");
      toast.success(`${tekenb.length} tekenbevoegde(n) opgeslagen`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt"); }
    setSaving(false);
  };

  const P = (title, val, setVal, prefix) => (
    <div className="mb-3">
      <h4 className="text-sm font-medium mb-1">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field label="Naam" val={val.naam} onChange={v => setVal({ ...val, naam: v })} testid={`${prefix}-naam`} />
        <Field label="Functie" val={val.functie} onChange={v => setVal({ ...val, functie: v })} testid={`${prefix}-func`} />
        <Field label="E-mail" val={val.email} onChange={v => setVal({ ...val, email: v })} testid={`${prefix}-mail`} />
        <Field label="Telefoon" val={val.telefoon} onChange={v => setVal({ ...val, telefoon: v })} testid={`${prefix}-tel`} />
      </div>
    </div>
  );

  return (
    <div>
      <h3 className="text-lg font-semibold mb-3">{step.label}</h3>

      {/* Repeatable tekenbevoegden */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium">Tekenbevoegden * ({tekenb.length})</h4>
          <span className="text-xs text-neutral-500">
            {primairCount === 1 ? "✓ 1 primair aanspreekpunt" :
              primairCount === 0 ? "⚠ Geen primair aanspreekpunt" :
              `⛔ ${primairCount} primair — mag maar 1`}
          </span>
        </div>
        {tekenb.map((t, idx) => (
          <div key={idx} className="border border-neutral-200 rounded-md p-3 mb-2 bg-white" data-testid={`tekenb-row-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-neutral-500">Tekenbevoegde #{idx + 1}</span>
              {tekenb.length > 1 && (
                <Button size="sm" variant="ghost" onClick={() => removeT(idx)}
                        data-testid={`tekenb-remove-${idx}`}
                        className="h-6 text-red-600 hover:bg-red-50">
                  Verwijderen
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Field label="Voornaam *" val={t.voornaam} onChange={v => updT(idx, { voornaam: v })} testid={`tekenb-vn-${idx}`} />
              <Field label="Achternaam *" val={t.achternaam} onChange={v => updT(idx, { achternaam: v })} testid={`tekenb-an-${idx}`} />
              <Field label="Functie" val={t.functie} onChange={v => updT(idx, { functie: v })} testid={`tekenb-fn-${idx}`} />
              <Field label="Zakelijk e-mailadres *" val={t.email} onChange={v => updT(idx, { email: v })} testid={`tekenb-em-${idx}`} />
              <Field label="Telefoon" val={t.telefoon} onChange={v => updT(idx, { telefoon: v })} testid={`tekenb-tel-${idx}`} />
              <div>
                <label className="text-xs text-neutral-600">Type bevoegdheid</label>
                <select value={t.bevoegdheid_type}
                        onChange={e => updT(idx, { bevoegdheid_type: e.target.value })}
                        data-testid={`tekenb-bev-${idx}`}
                        className="w-full h-9 rounded-md border border-neutral-300 px-2 text-sm bg-white">
                  <option value="zelfstandig">Zelfstandig bevoegd</option>
                  <option value="gezamenlijk">Gezamenlijk bevoegd</option>
                </select>
              </div>
            </div>
            <div className="mt-2 flex gap-4 items-center text-sm">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={t.primair_aanspreekpunt}
                       onChange={() => setPrimair(idx)}
                       data-testid={`tekenb-primair-${idx}`} />
                Primair aanspreekpunt
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={t.actief}
                       onChange={e => updT(idx, { actief: e.target.checked })}
                       data-testid={`tekenb-actief-${idx}`} />
                Actief
              </label>
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addT} data-testid="tekenb-add">
          + Tekenbevoegde toevoegen
        </Button>
      </div>

      {P("Financieel contactpersoon", fin, setFin, "fin")}
      {P("Privacy contactpersoon", priv, setPriv, "priv")}
      <Button onClick={save} disabled={saving} style={{ background: "var(--sage)" }} className="text-white" data-testid="cp-save">
        {saving ? "Opslaan…" : "Opslaan"}
      </Button>
    </div>
  );
}

function BrandingStep({ step, orgId, org, saveStep }) {
  const [f, setF] = useState({
    logo_url: org.logo_url || "", kleur_primair: org.kleur_primair || "#3a5a40",
    kleur_secundair: org.kleur_secundair || "#c9a86a", afzendernaam: org.afzendernaam || "",
    ouderapp_naam: org.ouderapp_naam || org.name,
  });
  const [uploading, setUploading] = useState(false);
  const save = async () => {
    try {
      await api.patch(`/organizations/${orgId}`, f);
      await saveStep(step.key, f, "compleet");
      // Pas branding direct toe op de :root CSS-variabelen
      const root = document.documentElement;
      if (f.kleur_primair) {
        root.style.setProperty("--sage", f.kleur_primair);
        root.style.setProperty("--sage-100", f.kleur_primair + "1a");
      }
      if (f.kleur_secundair) root.style.setProperty("--gold", f.kleur_secundair);
      toast.success("Branding opgeslagen — kleuren direct toegepast op de app");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Bestand mag max 5 MB zijn");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/organizations/${orgId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Bouw volledige URL: prefix met backend base
      const fullUrl = r.data.logo_url.startsWith("http") ? r.data.logo_url : `${process.env.REACT_APP_BACKEND_URL}${r.data.logo_url}`;
      setF(prev => ({ ...prev, logo_url: fullUrl }));
      toast.success("Logo geüpload");
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Logo</Label>
            <div className="flex items-center gap-3">
              {f.logo_url && (
                <img src={f.logo_url} alt="logo" className="w-14 h-14 rounded-lg object-cover border" data-testid="br-logo-current" />
              )}
              <div className="flex-1 space-y-2">
                <label className="block">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={e => uploadLogo(e.target.files?.[0])}
                    className="hidden" data-testid="br-logo-file" id="br-logo-input" />
                  <Button asChild variant="outline" disabled={uploading} className="cursor-pointer w-full" data-testid="br-logo-upload-btn">
                    <span onClick={() => document.getElementById("br-logo-input").click()}>
                      {uploading ? "Uploaden…" : "Bestand kiezen (PNG/JPG/WebP/SVG · max 5 MB)"}
                    </span>
                  </Button>
                </label>
                <Input value={f.logo_url} onChange={e => setF({ ...f, logo_url: e.target.value })} placeholder="Of plak een URL" className="text-xs" data-testid="br-logo-url" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Primaire kleur</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" value={f.kleur_primair} onChange={e => setF({ ...f, kleur_primair: e.target.value })} className="w-14 h-10 p-1 cursor-pointer" data-testid="br-color-primair" />
                <Input value={f.kleur_primair} onChange={e => setF({ ...f, kleur_primair: e.target.value })} className="font-mono text-xs" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Secundaire kleur</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" value={f.kleur_secundair} onChange={e => setF({ ...f, kleur_secundair: e.target.value })} className="w-14 h-10 p-1 cursor-pointer" data-testid="br-color-secundair" />
                <Input value={f.kleur_secundair} onChange={e => setF({ ...f, kleur_secundair: e.target.value })} className="font-mono text-xs" />
              </div>
            </div>
          </div>
          <Field label="Afzendernaam (e-mail)" val={f.afzendernaam} onChange={v => setF({ ...f, afzendernaam: v })} testid="br-afzender" />
          <Field label="Naam in ouderapp" val={f.ouderapp_naam} onChange={v => setF({ ...f, ouderapp_naam: v })} testid="br-ouderapp" />
          <Button onClick={save} style={{ background: "var(--sage)" }} className="text-white" data-testid="br-save">Opslaan</Button>
        </div>

        {/* Live preview van ouderapp */}
        <div data-testid="br-preview">
          <Label className="text-xs mb-1 block">Live preview — zo ziet de ouderapp eruit</Label>
          <BrandingPreview branding={f} />
        </div>
      </div>
    </div>
  );
}

function BrandingPreview({ branding }) {
  const primair = branding.kleur_primair || "#3a5a40";
  const secundair = branding.kleur_secundair || "#c9a86a";
  const naam = branding.ouderapp_naam || "Kinderopvang";
  return (
    <div className="rounded-2xl overflow-hidden border-4 border-neutral-800 shadow-lg max-w-[300px] mx-auto" style={{ background: "#fff" }} data-testid="br-preview-frame">
      {/* Statusbar (mock) */}
      <div className="h-4 bg-neutral-800"></div>
      {/* App header */}
      <div className="px-4 py-4 flex items-center gap-3" style={{ background: primair, color: "#fff" }}>
        {branding.logo_url ? (
          <img src={branding.logo_url} alt="logo" className="w-9 h-9 rounded-full bg-white object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold" style={{ background: "#fff", color: primair }}>
            {naam.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{naam}</div>
          <div className="text-[10px] opacity-80">Ouderportaal</div>
        </div>
      </div>
      {/* Content */}
      <div className="p-4 space-y-3">
        <div className="text-xs text-neutral-500">Vandaag</div>
        <div className="rounded-lg p-3 border" style={{ background: `${primair}10`, borderColor: `${primair}30` }}>
          <div className="text-xs font-medium" style={{ color: primair }}>Sofia is aangemeld</div>
          <div className="text-[10px] text-neutral-500 mt-0.5">Aangekomen om 08:30</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: `${secundair}20` }}>
          <div className="text-xs font-medium" style={{ color: "#333" }}>💌 Nieuw bericht van de leidster</div>
          <div className="text-[10px] text-neutral-600 mt-1">Sofia heeft heerlijk gespeeld met de blokken vandaag!</div>
        </div>
        <button className="w-full text-white text-xs font-medium py-2 rounded-lg" style={{ background: primair }}>
          Bekijk schriftje
        </button>
        <button className="w-full text-xs font-medium py-2 rounded-lg border" style={{ borderColor: secundair, color: "#333", background: "#fff" }}>
          Meld af voor morgen
        </button>
      </div>
      {/* Footer */}
      <div className="px-4 py-2 text-[9px] text-neutral-400 border-t text-center">
        Powered by Webster
      </div>
    </div>
  );
}

function BankStep({ step, orgId, saveStep }) {
  const [ents, setEnts] = useState([]);
  const [f, setF] = useState({ betaal_termijn_dagen: 14, betaal_methode: "incasso" });
  const load = async () => {
    const r = await api.get(`/organizations/${orgId}/entities`);
    setEnts(r.data.items || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const submit = async () => {
    if (!f.juridische_naam) return toast.error("Juridische naam verplicht");
    try {
      await api.post(`/organizations/${orgId}/entities`, f);
      await saveStep(step.key, { hasEntity: true }, "compleet");
      setF({ betaal_termijn_dagen: 14, betaal_methode: "incasso" });
      toast.success("Entiteit toegevoegd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      {ents.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Bestaande entiteiten</Label>
          {ents.map(e => (
            <div key={e.id} className="p-2 border rounded text-xs" data-testid={`bank-ent-${e.id}`}>
              <strong>{e.juridische_naam}</strong> · KVK {e.kvk || "—"} · IBAN {e.iban || "—"}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Juridische naam *" val={f.juridische_naam} onChange={v => setF({ ...f, juridische_naam: v })} testid="bk-naam" />
        <Field label="KVK" val={f.kvk} onChange={v => setF({ ...f, kvk: v })} testid="bk-kvk" />
        <Field label="IBAN (NL...)" val={f.iban} onChange={v => setF({ ...f, iban: v })} testid="bk-iban" />
        <Field label="Rekeninghouder" val={f.rekeninghouder} onChange={v => setF({ ...f, rekeninghouder: v })} testid="bk-houder" />
        <Field label="SEPA Incassant-ID" val={f.sepa_incassant_id} onChange={v => setF({ ...f, sepa_incassant_id: v })} testid="bk-sepa" />
        <Field label="Betaaltermijn (dagen)" val={f.betaal_termijn_dagen} onChange={v => setF({ ...f, betaal_termijn_dagen: parseInt(v || "0") })} testid="bk-termijn" />
      </div>
      <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="bk-save">Entiteit toevoegen</Button>
    </div>
  );
}

function ContractsStep({ step, orgId, saveStep }) {
  const [packs, setPacks] = useState([]);
  const [f, setF] = useState({ opvangsoort: "KDV", facturatie_eenheid: "maand", dagdeel: "hele_dag" });
  const load = async () => {
    const r = await api.get(`/organizations/${orgId}/contract-packages`);
    setPacks(r.data.items || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const submit = async () => {
    if (!f.naam || !f.uurtarief || !f.startdatum) return toast.error("Naam, uurtarief en startdatum verplicht");
    try {
      await api.post(`/organizations/${orgId}/contract-packages`, f);
      await saveStep(step.key, { hasPackage: true }, "compleet");
      setF({ opvangsoort: "KDV", facturatie_eenheid: "maand", dagdeel: "hele_dag" });
      toast.success("Pakket toegevoegd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      {packs.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Bestaande pakketten</Label>
          {packs.map(p => (
            <div key={p.id} className="p-2 border rounded text-xs" data-testid={`ct-pack-${p.id}`}>
              <strong>{p.naam}</strong> · {p.opvangsoort} · €{p.uurtarief.toFixed(2)}/u · vanaf {p.startdatum}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Naam *" val={f.naam} onChange={v => setF({ ...f, naam: v })} testid="ct-naam" />
        <div>
          <Label className="text-xs">Opvangsoort</Label>
          <Select value={f.opvangsoort} onValueChange={v => setF({ ...f, opvangsoort: v })}>
            <SelectTrigger data-testid="ct-opvang"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="KDV">KDV</SelectItem><SelectItem value="BSO">BSO</SelectItem><SelectItem value="PSZ">Peuterspeelzaal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Uurtarief * (€)" val={f.uurtarief} onChange={v => setF({ ...f, uurtarief: parseFloat(v || "0") })} testid="ct-tarief" />
        <Field label="Startdatum *" val={f.startdatum} onChange={v => setF({ ...f, startdatum: v })} placeholder="YYYY-MM-DD" testid="ct-start" />
        <Field label="Einddatum" val={f.einddatum} onChange={v => setF({ ...f, einddatum: v })} placeholder="YYYY-MM-DD" testid="ct-eind" />
        <Field label="Weken/jaar" val={f.weken_per_jaar} onChange={v => setF({ ...f, weken_per_jaar: parseInt(v || "0") })} testid="ct-weken" />
      </div>
      <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="ct-save">Pakket toevoegen</Button>
    </div>
  );
}

function OpeningHoursStep({ step, form, setForm, saveStep }) {
  const days = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
  const upd = (i, k, v) => {
    const hrs = [...(form.hours || days.map(() => ({ open: "07:30", close: "18:30", open_day: i < 5 })))];
    hrs[i] = { ...hrs[i], [k]: v };
    setForm({ ...form, hours: hrs });
  };
  const save = async () => {
    await saveStep(step.key, form, "compleet");
    toast.success("Openingstijden opgeslagen");
  };
  const hrs = form.hours || days.map((_, i) => ({ open: "07:30", close: "18:30", open_day: i < 5 }));
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      <div className="space-y-1.5">
        {days.map((d, i) => (
          <div key={d} className="flex items-center gap-2 text-sm">
            <div className="w-10 font-medium">{d}</div>
            <Checkbox checked={hrs[i].open_day} onCheckedChange={v => upd(i, "open_day", !!v)} data-testid={`oh-open-${i}`} />
            <span className="text-xs text-neutral-500">open</span>
            <Input type="time" value={hrs[i].open} onChange={e => upd(i, "open", e.target.value)} className="w-28" data-testid={`oh-from-${i}`} disabled={!hrs[i].open_day} />
            <Input type="time" value={hrs[i].close} onChange={e => upd(i, "close", e.target.value)} className="w-28" data-testid={`oh-to-${i}`} disabled={!hrs[i].open_day} />
          </div>
        ))}
      </div>
      <Button onClick={save} style={{ background: "var(--sage)" }} className="text-white" data-testid="oh-save">Opslaan</Button>
    </div>
  );
}

function PolicyDocumentStep({ step, orgId, saveStep }) {
  const [f, setF] = useState({ naam: step.label, versie: "1.0", ingangsdatum: new Date().toISOString().slice(0, 10) });
  const [docs, setDocs] = useState([]);
  const load = async () => {
    const r = await api.get(`/organizations/${orgId}/policy-documents`);
    setDocs((r.data.items || []).filter(d => d.document_type === step.policy));
  };
  useEffect(() => { load(); }, [step.policy]); // eslint-disable-line
  const submit = async () => {
    try {
      await api.post(`/organizations/${orgId}/policy-documents`, {
        ...f, document_type: step.policy,
      });
      await saveStep(step.key, { hasDoc: true }, "compleet");
      toast.success("Document geregistreerd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      {docs.length > 0 && (
        <div className="space-y-1">
          {docs.map(d => (
            <div key={d.id} className="p-2 border rounded text-xs">
              <strong>{d.naam}</strong> · v{d.versie} · vanaf {d.ingangsdatum} · {d.status}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Documentnaam" val={f.naam} onChange={v => setF({ ...f, naam: v })} testid="pd-naam" />
        <Field label="Versie" val={f.versie} onChange={v => setF({ ...f, versie: v })} testid="pd-versie" />
        <Field label="Ingangsdatum" val={f.ingangsdatum} onChange={v => setF({ ...f, ingangsdatum: v })} testid="pd-ingang" />
      </div>
      <Field label="Bestandslink (URL)" val={f.file_url} onChange={v => setF({ ...f, file_url: v })} testid="pd-url" />
      <div className="flex items-center gap-4 text-sm">
        <label className="flex items-center gap-1.5"><Checkbox checked={!!f.ouderakkoord_vereist} onCheckedChange={v => setF({ ...f, ouderakkoord_vereist: !!v })} data-testid="pd-akkoord" /> Ouderakkoord vereist</label>
        <label className="flex items-center gap-1.5"><Checkbox checked={!!f.digitale_ondertekening_vereist} onCheckedChange={v => setF({ ...f, digitale_ondertekening_vereist: !!v })} data-testid="pd-sign" /> Digitale ondertekening vereist</label>
      </div>
      <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="pd-save">Document registreren</Button>
    </div>
  );
}

function MigrationStep({ step, form, setForm, saveStep, orgId }) {
  const [batches, setBatches] = useState([]);
  const [provider, setProvider] = useState("kovnet");
  const [entity, setEntity] = useState("kinderen");
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsedPreview, setParsedPreview] = useState(null); // {rows, filename, headers}
  const [mapping, setMapping] = useState({}); // sourceCol → targetField
  const [showMapping, setShowMapping] = useState(false);

  // Doelvelden per entity — deze verwacht het backend
  const TARGET_FIELDS = {
    kinderen: [
      { key: "voornaam", label: "Voornaam *" },
      { key: "achternaam", label: "Achternaam *" },
      { key: "geboortedatum", label: "Geboortedatum (YYYY-MM-DD) *" },
      { key: "geslacht", label: "Geslacht (m/v/x)" },
      { key: "bsn", label: "BSN" },
    ],
    ouders: [
      { key: "voornaam", label: "Voornaam *" },
      { key: "achternaam", label: "Achternaam *" },
      { key: "email", label: "E-mail *" },
      { key: "telefoon", label: "Telefoon" },
    ],
    medewerkers: [
      { key: "naam", label: "Volledige naam *" },
      { key: "email", label: "E-mail *" },
      { key: "functie", label: "Functie" },
    ],
    locaties: [
      { key: "naam", label: "Naam *" },
      { key: "lrk", label: "LRK-nummer (9 cijfers)" },
      { key: "adres", label: "Adres" },
    ],
    contracten: [
      { key: "kind_ref", label: "Kind-referentie *" },
      { key: "startdatum", label: "Startdatum *" },
      { key: "einddatum", label: "Einddatum" },
    ],
  };

  // Auto-mapping op basis van veelvoorkomende KOVnet/Bitcare column names
  const AUTO_MAP = {
    // Kinderen
    voornaam: ["voornaam", "kind_voornaam", "child_first_name", "first_name", "firstname", "kind_first_name"],
    achternaam: ["achternaam", "kind_achternaam", "child_last_name", "last_name", "lastname", "kind_naam", "surname"],
    geboortedatum: ["geboortedatum", "birthdate", "date_of_birth", "dob", "birth_date", "geboorte"],
    geslacht: ["geslacht", "gender", "sex"],
    bsn: ["bsn", "burgerservicenummer"],
    // Ouders/medewerkers
    email: ["email", "e-mail", "emailadres", "mail", "e_mail"],
    telefoon: ["telefoon", "phone", "mobiel", "mobile", "tel"],
    naam: ["naam", "volledige_naam", "full_name", "name"],
    functie: ["functie", "role", "position"],
    // Locaties
    lrk: ["lrk", "lrk_nummer", "lrk_id", "registratienummer"],
    adres: ["adres", "address", "straat"],
    // Contracten
    kind_ref: ["kind_ref", "kind_id", "child_id", "kind"],
    startdatum: ["startdatum", "start_date", "begin_datum"],
    einddatum: ["einddatum", "end_date", "eind_datum"],
  };

  const autoMap = (headers, targetFields) => {
    const m = {};
    for (const h of headers) {
      const lower = h.toLowerCase().trim();
      for (const target of targetFields) {
        const candidates = AUTO_MAP[target.key] || [target.key];
        if (candidates.some(c => lower === c || lower.replace(/[_\s-]/g, "") === c.replace(/[_\s-]/g, ""))) {
          m[h] = target.key;
          break;
        }
      }
    }
    return m;
  };

  const load = async () => {
    if (!orgId) return;
    const r = await api.get(`/organizations/${orgId}/import/batches`);
    setBatches(r.data.items || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const parseCSV = (txt) => {
    const lines = txt.trim().split(/\r?\n/);
    if (lines.length < 2) return { rows: [], headers: [] };
    const headers = lines[0].split(/[;,\t]/).map(h => h.trim());
    const rows = lines.slice(1).map(line => {
      const parts = line.split(/[;,\t]/);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (parts[i] || "").trim(); });
      return obj;
    });
    return { rows, headers };
  };

  const handleFile = async (file) => {
    if (!file) return;
    const isXlsx = /\.xlsx?$/i.test(file.name);
    try {
      let rows = []; let headers = [];
      if (isXlsx) {
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const first = wb.SheetNames[0];
        const sheet = wb.Sheets[first];
        rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        headers = rows.length > 0 ? Object.keys(rows[0]) : [];
      } else {
        const txt = await file.text();
        const parsed = parseCSV(txt);
        rows = parsed.rows;
        headers = parsed.headers;
      }
      if (!rows.length) return toast.error("Geen rijen gevonden in bestand");
      const initialMap = autoMap(headers, TARGET_FIELDS[entity] || []);
      setParsedPreview({ rows, filename: file.name, headers });
      setMapping(initialMap);
      setCsvText("");
      const mappedCount = Object.keys(initialMap).length;
      toast.success(`${rows.length} rijen ingelezen · ${mappedCount}/${headers.length} kolommen auto-gemapt`);
    } catch (e) {
      toast.error(`Kan bestand niet lezen: ${e.message || e}`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // Herbereken mapping als user van entity switcht — probeer eerst opgeslagen mapping
  useEffect(() => {
    if (!parsedPreview || !orgId) return;
    (async () => {
      try {
        const r = await api.get(`/organizations/${orgId}/import-mappings`, { params: { provider, entity } });
        const saved = r.data.items?.[0]?.mapping;
        if (saved && Object.keys(saved).length) {
          // Filter op headers die nog bestaan in huidige file
          const filtered = {};
          Object.entries(saved).forEach(([k, v]) => {
            if (parsedPreview.headers.includes(k)) filtered[k] = v;
          });
          if (Object.keys(filtered).length) {
            setMapping(filtered);
            toast.success(`Opgeslagen mapping geladen (${Object.keys(filtered).length} kolommen)`);
            return;
          }
        }
      } catch { /* fallback */ }
      setMapping(autoMap(parsedPreview.headers, TARGET_FIELDS[entity] || []));
    })();
  }, [entity, provider]); // eslint-disable-line

  const applyMappingToRows = (rows, m) => {
    return rows.map(row => {
      const mapped = {};
      Object.entries(row).forEach(([k, v]) => {
        const target = m[k];
        if (target) mapped[target] = v;
      });
      return mapped;
    });
  };

  const uploadStage = async () => {
    let rows, headers;
    if (parsedPreview) {
      rows = parsedPreview.rows;
      headers = parsedPreview.headers;
    } else {
      if (!csvText.trim()) return toast.error("Sleep een bestand of plak CSV-data");
      const parsed = parseCSV(csvText);
      rows = parsed.rows; headers = parsed.headers;
      if (!rows.length) return toast.error("Geen geldige rijen gevonden");
    }
    // Controleer verplichte velden
    const targets = TARGET_FIELDS[entity] || [];
    const requiredFields = targets.filter(t => t.label.includes("*")).map(t => t.key);
    const mappedTargets = new Set(Object.values(mapping));
    const missing = requiredFields.filter(r => !mappedTargets.has(r));
    if (missing.length > 0) {
      toast.error(`Ontbrekende verplichte mapping: ${missing.join(", ")}`);
      setShowMapping(true);
      return;
    }
    const mappedRows = applyMappingToRows(rows, mapping);
    setBusy(true);
    try {
      const r = await api.post(`/organizations/${orgId}/import/batches`, { provider, entity, rows: mappedRows, mapping });
      // Sla mapping op voor volgende keer
      try { await api.put(`/organizations/${orgId}/import-mappings`, { provider, entity, mapping }); } catch { /* niet blokkerend */ }
      toast.success(`Batch aangemaakt met ${mappedRows.length} rijen — nu valideren`);
      setCsvText(""); setParsedPreview(null); setMapping({}); setShowMapping(false);
      await load();
      await validate(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const validate = async (batchId) => {
    setBusy(true);
    try {
      const r = await api.post(`/organizations/${orgId}/import/batches/${batchId}/validate`);
      const msg = `Validatie: ${r.data.ok} OK · ${r.data.fail} fouten · ${r.data.duplicates} duplicaten`;
      r.data.fail === 0 ? toast.success(msg) : toast.error(msg);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const commit = async (batchId) => {
    if (!confirm("Definitieve import uitvoeren? Dit maakt records aan in productie.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/organizations/${orgId}/import/batches/${batchId}/commit`);
      toast.success(`Geïmporteerd: ${r.data.committed} records`);
      await saveStep(step.key, { hasImport: true }, "compleet");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const rollback = async (batchId) => {
    if (!confirm("Deze import terugdraaien? Alle aangemaakte records verdwijnen.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/organizations/${orgId}/import/batches/${batchId}/rollback`);
      toast.success(`Rollback: ${r.data.removed} records verwijderd`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const showErrors = async (batchId) => {
    try {
      const r = await api.get(`/organizations/${orgId}/import/batches/${batchId}/rows?only_errors=true&limit=20`);
      setSelectedBatch({ id: batchId, errors: r.data.items || [] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badge = (status) => {
    const cfg = {
      staged: ["bg-neutral-100 text-neutral-700", "In staging"],
      validated: ["bg-emerald-100 text-emerald-700", "Gevalideerd"],
      committed: ["bg-sky-100 text-sky-700", "Geïmporteerd"],
      rolled_back: ["bg-amber-100 text-amber-700", "Teruggerold"],
      failed: ["bg-red-100 text-red-700", "Mislukt"],
    };
    const [cls, lbl] = cfg[status] || ["bg-neutral-100", status];
    return <Badge className={`${cls} border-transparent text-[10px]`}>{lbl}</Badge>;
  };

  const currentTargets = TARGET_FIELDS[entity] || [];
  const mappedTargets = new Set(Object.values(mapping));
  const requiredTargets = currentTargets.filter(t => t.label.includes("*")).map(t => t.key);
  const missingRequired = requiredTargets.filter(r => !mappedTargets.has(r));

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{step.label}</h3>
      <p className="text-sm text-neutral-600">
        Migreer kinderen, ouders, locaties en meer uit KOVnet, Bitcare of eigen XLSX/CSV. Import gaat eerst in <strong>staging</strong>,
        wordt gevalideerd, en pas na jouw bevestiging in productie geplaatst. Rollback blijft mogelijk zolang de data niet operationeel is gebruikt.
      </p>

      {/* Upload zone */}
      <Card className="p-3 bg-neutral-50 shadow-none border-neutral-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="mig-provider"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="kovnet">KOVnet</SelectItem>
                <SelectItem value="bitcare">Bitcare</SelectItem>
                <SelectItem value="csv">CSV/XLSX generiek</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Entiteit</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="mig-entity"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["kinderen","ouders","medewerkers","contracten","locaties","groepen","presentie","facturen","documenten","toestemmingen"].map(e =>
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={uploadStage} disabled={busy || (!parsedPreview && !csvText.trim())} style={{ background: "var(--sage)" }} className="text-white w-full" data-testid="mig-stage-btn">
              {busy ? "Bezig…" : parsedPreview ? `Stage ${parsedPreview.rows.length} rijen` : "Stage & valideer"}
            </Button>
          </div>
        </div>

        {/* Drop-zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("mig-file-input").click()}
          className={`mt-3 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[color:var(--sage)] bg-sage-50" : "border-neutral-300 hover:border-[color:var(--sage)] bg-white"}`}
          data-testid="mig-dropzone"
        >
          <input
            id="mig-file-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={e => handleFile(e.target.files?.[0])}
            data-testid="mig-file-input"
          />
          {parsedPreview ? (
            <div className="text-sm">
              <div className="font-medium text-[color:var(--sage)]">✓ {parsedPreview.filename}</div>
              <div className="text-xs text-neutral-600 mt-1">{parsedPreview.rows.length} rijen ingelezen · klik of sleep opnieuw om te vervangen</div>
              <div className="mt-2 text-[10px] text-neutral-500 font-mono truncate">Voorbeeld: {JSON.stringify(parsedPreview.rows[0]).slice(0, 120)}…</div>
            </div>
          ) : (
            <div className="text-sm text-neutral-600">
              <div className="font-medium">📄 Sleep XLSX/CSV-bestand hier</div>
              <div className="text-xs text-neutral-500 mt-1">Of klik om een bestand te kiezen (kolomkoppen op eerste regel)</div>
            </div>
          )}
        </div>

        {/* Optional textarea fallback */}
        <details className="mt-3">
          <summary className="text-xs text-neutral-500 cursor-pointer">Of plak CSV-data handmatig…</summary>
          <Textarea
            rows={4}
            value={csvText}
            onChange={e => { setCsvText(e.target.value); setParsedPreview(null); }}
            placeholder="voornaam;achternaam;geboortedatum&#10;Sofia;Van Dijk;2022-04-15"
            data-testid="mig-csv-textarea"
            className="font-mono text-xs mt-2"
          />
        </details>
      </Card>

      {/* Kolom-mapping UI */}
      {parsedPreview && (
        <Card className="p-3 bg-white shadow-none" data-testid="mig-mapping-card">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <h4 className="text-sm font-medium">Kolom-mapping</h4>
              <div className="text-xs text-neutral-600">
                {Object.keys(mapping).length}/{parsedPreview.headers.length} kolommen gemapt
                {missingRequired.length > 0 && (
                  <span className="ml-2 text-red-600">· ontbreekt: {missingRequired.join(", ")}</span>
                )}
                {missingRequired.length === 0 && (
                  <span className="ml-2 text-emerald-600">✓ verplichte velden compleet</span>
                )}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setShowMapping(!showMapping)} data-testid="mig-mapping-toggle">
              {showMapping ? "Verberg" : "Toon"} mapping
            </Button>
          </div>
          {showMapping && (
            <div className="space-y-1.5 mt-2">
              {parsedPreview.headers.map(h => {
                const currentTarget = mapping[h] || "";
                const isMapped = !!currentTarget;
                return (
                  <div key={h} className="flex items-center gap-2 text-xs" data-testid={`mig-map-${h}`}>
                    <div className={`flex-1 truncate p-1.5 rounded ${isMapped ? "bg-emerald-50 text-emerald-800" : "bg-neutral-100"}`}>
                      <span className="font-mono">{h}</span>
                      <span className="text-neutral-500 text-[10px] ml-2 truncate">
                        (voorbeeld: {String(parsedPreview.rows[0]?.[h] || "").slice(0, 30)})
                      </span>
                    </div>
                    <span className="text-neutral-400">→</span>
                    <Select
                      value={currentTarget || "__none__"}
                      onValueChange={v => {
                        const newMap = { ...mapping };
                        if (v === "__none__") delete newMap[h];
                        else newMap[h] = v;
                        setMapping(newMap);
                      }}
                    >
                      <SelectTrigger className="w-56 h-8 text-xs" data-testid={`mig-map-select-${h}`}>
                        <SelectValue placeholder="— negeer —" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__none__">— negeer —</SelectItem>
                        {currentTargets.map(t => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Batches lijst */}
      {batches.length > 0 && (
        <Card className="p-3 bg-white shadow-none">
          <h4 className="text-sm font-medium mb-2">Import-batches ({batches.length})</h4>
          <div className="space-y-2">
            {batches.map(b => (
              <div key={b.id} className="flex items-center gap-2 text-xs border-b py-2" data-testid={`mig-batch-${b.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{b.entity} · {b.row_count} rijen · {b.provider}</div>
                  <div className="text-neutral-500">
                    {new Date(b.created_at).toLocaleString("nl-NL")}
                    {b.validated_ok !== undefined && ` · ${b.validated_ok}✓ / ${b.validated_fail}✗ ${b.validated_dup ? `/ ${b.validated_dup} dup` : ""}`}
                    {b.committed_count && ` · ${b.committed_count} geïmporteerd`}
                  </div>
                </div>
                {badge(b.status)}
                <div className="flex gap-1">
                  {b.status === "staged" && (
                    <Button size="sm" variant="ghost" onClick={() => validate(b.id)} disabled={busy} className="h-7 text-[10px]" data-testid={`mig-val-${b.id}`}>Valideer</Button>
                  )}
                  {b.status === "validated" && (
                    <Button size="sm" onClick={() => commit(b.id)} disabled={busy} style={{ background: "var(--sage)" }} className="text-white h-7 text-[10px]" data-testid={`mig-commit-${b.id}`}>Importeer</Button>
                  )}
                  {b.status === "committed" && (
                    <Button size="sm" variant="outline" onClick={() => rollback(b.id)} disabled={busy} className="h-7 text-[10px] border-amber-300 text-amber-700" data-testid={`mig-rollback-${b.id}`}>Rollback</Button>
                  )}
                  {(b.validated_fail > 0 || b.validated_dup > 0) && (
                    <Button size="sm" variant="ghost" onClick={() => showErrors(b.id)} className="h-7 text-[10px] text-red-700" data-testid={`mig-errors-${b.id}`}>Fouten</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Fouten-details */}
      {selectedBatch && (
        <Card className="p-3 bg-red-50 border-red-200 shadow-none">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-red-800">Foutenrapport ({selectedBatch.errors.length})</h4>
            <button onClick={() => setSelectedBatch(null)} className="text-xs text-neutral-600">Sluiten ✕</button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {selectedBatch.errors.map((r, i) => (
              <div key={i} className="text-[11px] bg-white p-1.5 rounded" data-testid={`mig-err-row-${i}`}>
                <div className="text-red-700">Rij {r.row_index + 1}: {r.error || "Onbekende fout"}</div>
                <div className="text-neutral-500 font-mono truncate">{JSON.stringify(r.raw)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, val, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={val || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}


function SigningStep({ step, orgId, saveStep }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ subdomain: "", api_key: "", from_email: "" });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testForm, setTestForm] = useState({ parent_name: "", parent_email: "", document_url: "" });
  const [testResult, setTestResult] = useState(null);
  const [requests, setRequests] = useState([]);

  const loadCfg = async () => {
    try {
      const r = await api.get(`/organizations/${orgId}/signrequest-config`);
      setCfg(r.data);
    } catch (e) { /* ignore */ }
  };
  const loadReqs = async () => {
    try {
      const r = await api.get(`/organizations/${orgId}/signing-requests`);
      setRequests(r.data.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadCfg(); loadReqs(); }, []); // eslint-disable-line

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/organizations/${orgId}/signrequest-config`, form);
      toast.success("SignRequest configuratie opgeslagen");
      setForm({ subdomain: "", api_key: "", from_email: "" });
      await loadCfg();
      await saveStep(step.key, { configured: true }, "compleet");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const verifyConnection = async () => {
    setVerifying(true);
    try {
      const r = await api.post(`/organizations/${orgId}/signrequest-config/verify`);
      toast.success(r.data.message || "Verbinding geslaagd");
      await loadCfg();
      await saveStep(step.key, { configured: true, verified: true }, "compleet");
    } catch (e) { toast.error(formatApiError(e)); } finally { setVerifying(false); }
  };

  const skipConfig = async () => {
    await saveStep(step.key, { configured: false, skipped: true }, "gedeeltelijk");
    toast.success("Module blijft in mock-modus — je kunt later koppelen");
  };

  const removeConfig = async () => {
    if (!confirm("Configuratie verwijderen? De module gaat terug naar mock-modus.")) return;
    await api.delete(`/organizations/${orgId}/signrequest-config`);
    toast.success("Configuratie verwijderd");
    loadCfg();
  };

  const createTest = async () => {
    if (!testForm.parent_email || !testForm.parent_name) return toast.error("Naam + e-mail verplicht");
    if (cfg?.mode === "live" && !testForm.document_url) return toast.error("Document URL verplicht in live modus");
    try {
      const r = await api.post(`/organizations/${orgId}/signing-requests`, testForm);
      setTestResult(r.data);
      toast.success(`Verzoek aangemaakt (${r.data.mode})`);
      loadReqs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mockSign = async (id) => {
    try {
      await api.post(`/organizations/${orgId}/signing-requests/${id}/mock-sign`);
      toast.success("Getekend (mock)");
      loadReqs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const modeBadge = cfg?.mode === "live"
    ? <Badge className="bg-emerald-100 text-emerald-800 border-transparent">Live modus</Badge>
    : <Badge className="bg-amber-100 text-amber-800 border-transparent">Mock-modus (testen)</Badge>;

  return (
    <div className="space-y-4" data-testid="onb-panel-digitaal_ondertekenen">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-lg font-semibold">{step.label}</h3>
        {cfg && modeBadge}
      </div>
      <p className="text-sm text-neutral-600">
        Laat ouders de plaatsingsovereenkomst online tekenen via SignRequest. De module werkt <strong>zonder koppeling in mock-modus</strong> — je kunt de flow uitproberen zonder echte verzendingen. Zodra je een SignRequest-account hebt, koppel je per tenant je eigen API-key. <a href="https://signrequest.com/#/api-settings" target="_blank" rel="noreferrer" className="underline text-[color:var(--sage)]">API-key ophalen bij SignRequest →</a>
      </p>

      {/* Configuratie */}
      <Card className="p-4 bg-white shadow-none">
        <h4 className="text-sm font-medium mb-2">SignRequest-koppeling {cfg?.configured && "(bestaat al)"}</h4>
        {cfg?.configured && (
          <div className="mb-3 p-2 bg-neutral-50 border rounded text-xs">
            <div><strong>Subdomain:</strong> {cfg.subdomain || "—"}</div>
            <div><strong>From-email:</strong> {cfg.from_email || "—"}</div>
            <div><strong>API-key:</strong> <span className="font-mono">{cfg.api_key_masked || "—"}</span></div>
            <div><strong>Modus:</strong> {cfg.mode}</div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Subdomain</Label>
            <Input value={form.subdomain} onChange={e => setForm({ ...form, subdomain: e.target.value })} placeholder="bijv. jouw-kdv" data-testid="sr-subdomain" />
          </div>
          <div>
            <Label className="text-xs">Verified from-email</Label>
            <Input value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })} placeholder="contracten@jouw-kdv.nl" data-testid="sr-fromemail" />
          </div>
          <div>
            <Label className="text-xs">API-key</Label>
            <Input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="SignRequest API-token" data-testid="sr-apikey" />
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <Button onClick={saveConfig} disabled={saving} style={{ background: "var(--sage)" }} className="text-white" data-testid="sr-save">
            {saving ? "Opslaan…" : "Opslaan"}
          </Button>
          {!cfg?.configured && (
            <Button onClick={skipConfig} variant="outline" data-testid="sr-skip">Later instellen (mock-modus)</Button>
          )}
          {cfg?.configured && (
            <Button onClick={verifyConnection} disabled={verifying} variant="outline" className="border-emerald-300 text-emerald-800" data-testid="sr-verify">
              {verifying ? "Testen…" : "Verifieer connectie"}
            </Button>
          )}
          {cfg?.configured && (
            <Button onClick={removeConfig} variant="outline" className="border-red-300 text-red-700" data-testid="sr-remove">Koppeling verwijderen</Button>
          )}
        </div>
      </Card>

      {/* Test-verzoek */}
      <Card className="p-4 bg-white shadow-none">
        <h4 className="text-sm font-medium mb-2">
          Test-verzoek {cfg?.mode === "mock" && "(alleen mock)"}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Naam ouder</Label>
            <Input value={testForm.parent_name} onChange={e => setTestForm({ ...testForm, parent_name: e.target.value })} data-testid="sr-test-name" />
          </div>
          <div>
            <Label className="text-xs">E-mail ouder</Label>
            <Input value={testForm.parent_email} onChange={e => setTestForm({ ...testForm, parent_email: e.target.value })} data-testid="sr-test-email" />
          </div>
          <div>
            <Label className="text-xs">Document URL {cfg?.mode !== "live" && "(optioneel in mock)"}</Label>
            <Input value={testForm.document_url} onChange={e => setTestForm({ ...testForm, document_url: e.target.value })} placeholder="https://…/contract.pdf" data-testid="sr-test-url" />
          </div>
        </div>
        <Button onClick={createTest} className="mt-3" style={{ background: "var(--sage)" }} data-testid="sr-test-btn">Verstuur test-verzoek</Button>
      </Card>

      {/* Verzoeken lijst */}
      {requests.length > 0 && (
        <Card className="p-3 bg-white shadow-none">
          <h4 className="text-sm font-medium mb-2">Ondertekeningsverzoeken ({requests.length})</h4>
          <div className="space-y-1.5">
            {requests.slice(0, 10).map(r => (
              <div key={r.id} className="flex items-center gap-2 text-xs border-b py-1.5" data-testid={`sr-req-${r.id}`}>
                <div className="flex-1 truncate">
                  <div className="font-medium">{r.parent_name} · {r.parent_email}</div>
                  <div className="text-neutral-500">{r.subject} · {new Date(r.created_at).toLocaleString("nl-NL")}</div>
                </div>
                <Badge className={`text-[10px] ${r.status === "signed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-transparent`}>
                  {r.status}
                </Badge>
                <Badge variant="outline" className="text-[9px]">{r.mode}</Badge>
                {r.mode === "mock" && r.status !== "signed" && (
                  <Button size="sm" variant="ghost" onClick={() => mockSign(r.id)} className="h-6 text-[10px]" data-testid={`sr-mocksign-${r.id}`}>
                    Simuleer tekenen
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
